<script setup lang="ts">
import { MapComponent } from '@sembo-private/vue3-maps';
import { MapControl } from '@sembo-private/maps';

import { AppButton } from '@libs/ui';

import type { MapConfig } from './Maps.model';

interface DynamicFullScreenMapProps {
  mapConfiguration: MapConfig;
}

const props = defineProps<DynamicFullScreenMapProps>();

interface DynamicFullScreenMapEmit {
  (event: 'close'): void;
}

const emit = defineEmits<DynamicFullScreenMapEmit>();
</script>

<template>
  <div class="dynamic-full-screen-map">
    <div class="dynamic-map-wrapper">
      <MapComponent
        gesture-handling="auto"
        :show-all-markers="false"
        :markers="[props.mapConfiguration.markers]"
        :marker-formatters="props.mapConfiguration.markerFormatters"
        :start-rectangle="props.mapConfiguration.startRectangle"
        :map-controls="[MapControl.zoomControl]"
      />
      <AppButton
        class="close-button"
        type="secondary"
        state="enabled"
        size="medium"
        :icon="{ name: 'close' }"
        @click="emit('close')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.dynamic-full-screen-map {
  --map-spacing: var(--spacing-32);

  width: 100vw;
  height: 100dvh;
  height: 100vh;
  position: fixed;
  z-index: var(--panel-second-level-z-index);
  background-color: rgba(0, 0, 0, 0.9);
  @include for-large-devices-min {
    padding: var(--map-spacing) 0;
  }

  .dynamic-map-wrapper {
    @include for-large-devices-min {
      width: 70%;
      min-width: 960px;
      max-height: calc(100% - var(--map-spacing) * 2);
      aspect-ratio: 7/5;
      border-radius: var(--border-radius-12);
      overflow: hidden;
      margin: 0 auto;
    }
    position: relative;
    width: 100%;
    height: 100dvh;

    .close-button {
      position: absolute;
      top: 0;
      right: 0;
      margin: var(--spacing-24);
      box-shadow: var(--shadow);
      cursor: pointer;
    }
  }
}
</style>

<style lang="scss">
.dynamic-full-screen-map {
  --minus-plus-size: 34px;
  .trip-planner-details-accommodation-marker,
  .trip-planner-details-activity-marker {
    height: 34px;
    width: 34px;
  }

  // NOTE: This fixes google map implementation bug.
  // Details: https://stackoverflow.com/questions/66669173/google-maps-js-api-shows-blue-focus-border
  .gm-style iframe + div {
    border: none !important;
  }
  // Zoom button
  #map .gm-style .gmnoprint > .gmnoprint > div {
    box-shadow: none !important;
    overflow: hidden;
    border-radius: var(--border-radius-12) !important;
    border: 1px solid var(--color-secondary-40);
    width: 40px !important;
    height: var(--spacing-64) !important;

    display: flex;
    flex-direction: column;

    //NOTE: map zoom control position on desktop needs to be adjusted manually
    position: absolute;
    right: -25px;
    bottom: -75px;
  }

  #map .gm-style .gmnoprint > .gmnoprint > div > button > img {
    display: none !important;
  }

  #map .gm-style .gmnoprint > .gmnoprint > div > button:first-child {
    height: var(--minus-plus-size) !important;
    background: url('../../../assets/icon/scss/map/plus-primary-20.svg') center
      no-repeat !important;
    transition: background var(--transition-short);
  }

  #map .gm-style .gmnoprint > .gmnoprint > div > div {
    background: var(--color-secondary-90);
    z-index: 10;
  }

  #map .gm-style .gmnoprint > .gmnoprint > div > button:last-child {
    height: var(--minus-plus-size) !important;
    background: url('../../../assets/icon/scss/map/minus-primary-20.svg') center
      no-repeat !important;
    transition: background var(--transition-short);
  }
  #map .gm-style .gmnoprint > .gmnoprint > div > button:first-child {
    @include on-hover {
      background:
        url('../../../assets/icon/scss/map/plus-primary-20.svg') center
          no-repeat,
        var(--color-secondary-97) !important;
    }
    border-top-left-radius: var(--border-radius-12);
    border-top-right-radius: var(--border-radius-12);
  }

  #map .gm-style .gmnoprint > .gmnoprint > div > button:last-child {
    @include on-hover {
      background:
        url('../../../assets/icon/scss/map/minus-primary-20.svg') center
          no-repeat,
        var(--color-secondary-97) !important;
      border-bottom-left-radius: var(--border-radius-12);
      border-bottom-right-radius: var(--border-radius-12);
    }
  }
}
</style>
